import React from 'react';
import { Box } from '@lucid/lego';
import type {
  VehicleOrderCheckoutDetail,
  VehicleOrderVehicleInfoItem,
} from '../../../../../api/services/lupp/order/importer/dto.importer';
import Feature from '../../../../../components/Checkout/VehicleCard/Feature/Feature';
import Price from '../../../../../components/Checkout/VehicleCard/Price/Price';
import styles from './VehicleConfigurationPanel.module.scss';

export type VehicleConfigurationPanelProps = {
  dataTestId: string;
  checkoutDetail: VehicleOrderCheckoutDetail;
  theme: 'dark' | 'light';
  /** i18n labels */
  labels?: Record<string, string>;
};

/**
 * VehicleConfigurationPanel displays the vehicle configuration options and pricing
 * from the checkoutDetail of a vehicle order. Reuses the Feature and Price components
 * from the Agency Sales / ProspectDesk flow.
 * @component
 * @param props.checkoutDetail - The checkout detail from the vehicle order
 * @param props.theme - Theme for the component
 * @param props.labels - i18n labels (keys: vehicle.included, vehicle.options, vehicle.total)
 * @param props.dataTestId - Test ID for automated testing
 * @returns JSX.Element | null — returns null when checkoutInfo is absent or empty
 * @example
 * <VehicleConfigurationPanel
 *   checkoutDetail={order.checkoutDetail}
 *   theme="dark"
 *   labels={labels}
 *   dataTestId="vehicle-configuration-panel"
 * />
 */
const VehicleConfigurationPanel: React.FC<VehicleConfigurationPanelProps> = ({
  dataTestId,
  checkoutDetail,
  theme,
  labels,
}) => {
  const { checkoutInfo } = checkoutDetail;

  if (!checkoutInfo) return null;

  const vehicleInfo = checkoutInfo.vehicleInfo ?? [];
  const pricing = checkoutInfo.pricing;

  // Split vehicleInfo into options and features — same logic as transformers.checkout.ts
  const modelOption = vehicleInfo.find((item) => item.label === 'model');
  const options = vehicleInfo.filter(
    (item) => item.label !== 'features' && item.label !== 'model' && !!item.label
  );
  const featureContents: VehicleOrderVehicleInfoItem[] =
    vehicleInfo.find((item) => item.label === 'features')?.contents ?? [];

  const getOptionDescription = (option: VehicleOrderVehicleInfoItem): string => {
    const optionName = option.name ?? '';

    return optionName;
  };

  const hasOptions = !!modelOption || options.length > 0 || featureContents.length > 0;
  const hasPricing = !!pricing;

  if (!hasOptions && !hasPricing) return null;

  return (
    <Box className={styles.root} data-testid={dataTestId}>
      {/* Vehicle model, modelYear and configuration options */}
      {(modelOption || checkoutInfo.modelYear || options.length > 0) && (
        <Box className={styles.section}>
          {modelOption && (
            <Feature
              key={modelOption.code ?? 'option-model'}
              description={labels?.['vehicle.model'] ?? 'Model'}
              price={modelOption.value ?? 0}
              theme={theme}
              labels={labels}
              value={modelOption.name}
            />
          )}
          {checkoutInfo.modelYear && (
            <Feature
              key={'option-model-year'}
              description={labels?.['vehicle.modelYear'] ?? 'Model Year'}
              price={0}
              theme={theme}
              labels={labels}
              value={checkoutInfo.modelYear}
            />
          )}
          {options.map((option, index) => (
            <Feature
              key={option.code ?? `option-${index}`}
              description={getOptionDescription(option)}
              price={option.value ?? 0}
              theme={theme}
              labels={labels}
            />
          ))}
        </Box>
      )}

      {/* Features (sound system, driver assistance, packages, etc.) */}
      {featureContents.length > 0 && (
        <Box className={styles.section}>
          {featureContents.map((feature, index) => (
            <Feature
              key={feature.code ?? `feature-${index}`}
              description={feature.name ?? ''}
              price={feature.value ?? 0}
              theme={theme}
              labels={labels}
            />
          ))}
        </Box>
      )}

      {/* Pricing breakdown */}
      {hasPricing && (
        <Box className={styles.pricingSection}>
          <Box className={styles.divider} />
          {typeof pricing.addOns?.value === 'number' && (
            <Price
              label={labels?.['vehicle.options'] ?? 'Options'}
              value={pricing.addOns.value}
              theme={theme}
              skipFormatting
            />
          )}
          {pricing.fees?.map((fee, index) => (
            <Price
              key={`fee-${index}`}
              label={fee.name ?? ''}
              value={fee.value ?? 0}
              theme={theme}
            />
          ))}
          {pricing.credits
            ?.filter((credit) => credit.name !== 'RESERVATION_PAYMENT')
            .map((credit, index) => (
              <Price
                key={`credit-${index}`}
                label={credit.name ?? ''}
                value={credit.value ?? 0}
                theme={theme}
                isNegative
              />
            ))}
          <Box className={styles.divider} />
          {typeof pricing.total?.value === 'number' && (
            <Price
              label={labels?.['vehicle.total'] ?? 'Total'}
              value={pricing.total.value}
              theme={theme}
              isBold
              skipFormatting
            />
          )}
        </Box>
      )}
    </Box>
  );
};

VehicleConfigurationPanel.displayName = 'VehicleConfigurationPanel';

export default VehicleConfigurationPanel;
